import { useId } from 'react';

import { cn } from '@/lib/utils';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** An occasional number beside the label. */
  count?: number;
}

/**
 * A choice of how one page shows its data: by profile or by workflow (spec 224
 * review).
 *
 * Not a tab strip. Tabs move between pages; this changes the view of the page in
 * front of the reader, so it is drawn as the period selector is — a segmented set
 * of radios with visible pills — and the semantics an assistive technology sees are
 * the real ones: one value chosen from a few.
 */
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  /** Names the choice for assistive technology, and leads it on screen. */
  label: string;
  options: SegmentedOption<T>[];
  value: T;
  onChange: (next: T) => void;
}) {
  const name = useId();
  const labelId = `${name}-label`;
  return (
    // On a phone the options share the row the label leaves (spec 277).
    <div className="flex min-w-0 flex-wrap items-center gap-2 max-md:w-full max-md:flex-nowrap">
      <span id={labelId} className="text-muted-foreground text-xs">
        {label}
      </span>
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        // Scrolls rather than squeezes on a phone: five groupings outrun 390px.
        className="border-border bg-muted/40 flex max-w-full overflow-x-auto rounded-md border max-md:flex-1"
      >
        {options.map((option) => {
          const optionId = `${name}-${option.value}`;
          const checked = option.value === value;
          return (
            <span
              key={option.value}
              className="relative shrink-0 max-md:flex-1"
            >
              <input
                type="radio"
                id={optionId}
                name={name}
                value={option.value}
                checked={checked}
                onChange={() => {
                  if (!checked) onChange(option.value);
                }}
                className="peer absolute inset-0 opacity-0"
              />
              <label
                htmlFor={optionId}
                className={cn(
                  'touch-target block cursor-pointer rounded-sm px-2.5 py-1.5 text-sm font-medium whitespace-nowrap transition-colors max-md:flex max-md:items-center max-md:justify-center',
                  'peer-checked:bg-card peer-checked:text-primary peer-checked:shadow-raised peer-checked:font-semibold',
                  'peer-focus-visible:ring-ring peer-focus-visible:ring-3 peer-focus-visible:outline-none',
                  'text-muted-foreground hover:text-foreground',
                )}
              >
                {option.label}
                {option.count !== undefined && option.count > 0 ? (
                  <span className="text-muted-foreground ml-1.5 text-xs font-normal tabular-nums">
                    {option.count}
                  </span>
                ) : null}
              </label>
            </span>
          );
        })}
      </div>
    </div>
  );
}
